/**
 * affiliate-system-fields.js
 *
 * Per-affiliate-system field configuration for the VoonixAdvertiser Create form.
 *
 * The SpaceAdvertiserLogin schema has fixed fields: loginUrl, username, password, key1, key2.
 * For known affiliate systems we only expose the slots that are REQUIRED — optional fields
 * are hidden from the UI to avoid clutter and accidental misuse.
 *
 * If a system isn't listed here, key1/key2 are not shown at all. Add an entry to expose them.
 *
 * Lookup is case-insensitive on the affiliate system `name`.
 */

const VOONIX_DOCS_BASE = 'https://helpcenter.voonix.net/brandintegration.html';

const SYSTEM_FIELDS = {
    softswiss: {
        docsAnchor: 'softswiss',
        helpSection: 'key-softswiss',   // internal /help slug for "how to get key1"
        key1: {
            label: 'Statistic token',
            hint: 'From the partner panel: side menu → My account → Account info → copy the Statistic token (32-char hex).'
        },
    },
    referon:        { docsAnchor: 'referon' },
    cellxpert:      { docsAnchor: 'cellxpert' },
    myaffiliates: {
        docsAnchor: 'myaffiliates',
        key1: {
            label: 'OAuth Key 1',
            hint: 'Generated manually when reCAPTCHA is enabled on the account (almost always the case). See Voonix docs.'
        },
        key2: {
            label: 'OAuth Key 2',
            hint: 'Generated manually when reCAPTCHA is enabled on the account (almost always the case). See Voonix docs.'
        },
    },
    netrefer:       { docsAnchor: 'netrefer' },
    affise:         { docsAnchor: 'affise' },
    mexos: {
        docsAnchor: 'mexos',
        key1: {
            label: 'Brand ID (numeric)',
            hint: 'From browser DevTools → Network tab → look for the numeric "id" value.'
        },
        key2: {
            label: 'Campaign ID',
            hint: 'From browser DevTools → Network tab → look for "Campaign ID".'
        },
    },
    'income access': {
        docsAnchor: 'incomeaccess',
        key1: {
            label: 'API Key',
            hint: '32-character key from the API Reports section — make sure to activate and submit.'
        },
    },
    'netrefer api': {
        docsAnchor: 'netreferapi',
        key1: {
            label: 'API Key',
            hint: 'Generated from the XLM Feeds menu.'
        },
        key2: {
            label: 'Tenant Name',
            hint: 'The "tenantName" parameter — usually the programme name (e.g. "playamopartners", "betfair").'
        },
    },
};

/**
 * Returns the Voonix docs URL for the given affiliate system, or the
 * generic brand integration index if there's no system-specific anchor.
 *
 * @param {string} systemName
 * @returns {string}
 */
export function getVoonixDocsUrl(systemName) {
    if (!systemName) return VOONIX_DOCS_BASE;
    const override = SYSTEM_FIELDS[systemName.toLowerCase()];
    return override?.docsAnchor
        ? `${VOONIX_DOCS_BASE}#${override.docsAnchor}`
        : VOONIX_DOCS_BASE;
}

/**
 * Returns the field config for a given affiliate system name.
 * Username and password are always required.
 * key1/key2 are only included if the system needs them — all are required when present.
 *
 * @param {string} systemName
 * @returns {{ username, password, key1?, key2? }}
 */
export function getFieldsForSystem(systemName) {
    const base = {
        username: { label: 'Username', required: true },
        password: { label: 'Password', required: true },
    };

    if (!systemName) return base;
    const override = SYSTEM_FIELDS[systemName.toLowerCase()];
    if (!override) return base;

    if (override.key1) base.key1 = { ...override.key1, required: true };
    if (override.key2) base.key2 = { ...override.key2, required: true };
    return base;
}

/**
 * Validates a SpaceAdvertiserLogin payload against the system config.
 * Returns { fieldKey: errorMessage } for any missing required field.
 *
 * @param {string} systemName
 * @param {object} login — { username, password, key1, key2 }
 */
export function validateLoginFields(systemName, login = {}) {
    const config = getFieldsForSystem(systemName);
    const errors = {};
    for (const field of Object.keys(config)) {
        const cfg = config[field];
        const value = (login[field] || '').toString().trim();
        if (cfg.required && !value) {
            errors[field] = `${cfg.label} is required for ${systemName}.`;
        }
    }
    return errors;
}

/**
 * Returns the internal /help deep-link slug for this system's setup docs,
 * or null if none is configured. Used to route the "how do I fix this"
 * click from a missing-required-field warning on the VoonixAdvertisers page.
 *
 * @param {string} systemName
 * @returns {string | null}
 */
export function getHelpSectionForSystem(systemName) {
    if (!systemName) return null;
    const override = SYSTEM_FIELDS[systemName.toLowerCase()];
    return override?.helpSection || null;
}
